import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Api } from './api';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  private readonly CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

  private permissions: any = {};
  private permissions$ = new BehaviorSubject<any>({});
  private lastToken: string = '';

  constructor(private router: Router, private api: Api) {}

  //  LOAD FROM API (GLOBAL FUNCTION)
  // loadPermissions(token: string, redirect: boolean = false) {
  //   this.lastToken = token;

  //   this.api.get('users/permissions', token).subscribe({
  //     next: (res: any) => {

  //       const permissions = res.data;

  //       this.setPermissions(permissions);

  //       if (redirect) {
  //         const firstRoute = this.getFirstAllowedRoute();
  //         this.router.navigateByUrl(firstRoute);
  //       }
  //     },
  //     error: (err) => {
  //       console.error('Permission load failed', err);
  //     }
  //   });
  // }
  loadPermissions(token: string, redirect: boolean = false): Promise<void> {
  this.lastToken = token;

  return new Promise((resolve, reject) => {

    this.api.get('users/permissions', token).subscribe({
      next: (res: any) => {

        const permissions = res.data;
        this.setPermissions(permissions);

        if (redirect) {
          const firstRoute = this.getFirstAllowedRoute();
          this.router.navigateByUrl(firstRoute);
        }

        resolve(); //  IMPORTANT
      },
      error: (err) => {
        console.error('Permission load failed', err);
        reject(err);
      }
    });

  });
}

  //  SET + STORE
  setPermissions(data: any) {
    const normalized = this.normalizePermissions(data);

    this.permissions = normalized;
    this.permissions$.next(normalized);

    localStorage.setItem('permissions', JSON.stringify(data));
    localStorage.setItem('permissionsSetAt', Date.now().toString());
  }

  //  GET (AUTO RESTORE AFTER REFRESH)
  getPermissions() {

    if (!this.permissions || Object.keys(this.permissions).length === 0) {

      const stored = localStorage.getItem('permissions');

      if (stored) {
        this.permissions = this.normalizePermissions(JSON.parse(stored));
      }
    }

    if (this.isCacheExpired() && this.lastToken) {
      this.loadPermissions(this.lastToken);
    }

    return this.permissions;
  }

  private isCacheExpired(): boolean {
    const setAt = localStorage.getItem('permissionsSetAt');
    if (!setAt) return true;
    return Date.now() - parseInt(setAt, 10) > this.CACHE_DURATION;
  }

  //  OBSERVABLE (for UI auto update)
  getPermissionsObservable() {
    return this.permissions$.asObservable();
  }

  //  NORMALIZER
  normalizePermissions(data: any) {
    const map: any = {};

    if (!data) return map;

    if (Array.isArray(data)) {
      data.forEach((p: any) => {
        if (!map[p.module]) {
          map[p.module] = [];
        }
        map[p.module].push(p.slug);
      });
      return map;
    }

    if (typeof data === 'object') {
      Object.keys(data).forEach(module => {

        const value = data[module];

        if (Array.isArray(value)) {
          map[module] = value.map((p: any) => p.slug);
        }
        else if (value === true) {
          map[module] = [module];
        }
      });
      return map;
    }

    return map;
  }

  //  CHECKS (ALWAYS USE getPermissions)
  hasPermission(module: string, slug: string): boolean {
    const perms = this.getPermissions();
    return perms?.[module]?.includes(slug) ?? false;
  }

  hasModule(module: string): boolean {
    const perms = this.getPermissions();
    return Array.isArray(perms[module]) && perms[module].length > 0;
  }

  //  ROUTES
  getAllowedRoutes(): string[] {

    const routes: string[] = [];

    if (this.hasPermission('dashboard', 'dashboard-dashboard')) {
      routes.push('/dashboard');
    }

    if (this.hasPermission('booking', 'booking-view')) {
      routes.push('/home');
    }

    if (this.hasPermission('payments', 'payments-list')) {
      routes.push('/payments');
    }

    if (this.hasPermission('coupons', 'coupons-list')) {
      routes.push('/fields');
    }

    if (this.hasPermission('games', 'games-list')) {
      routes.push('/games');
    }

    if (this.hasPermission('players', 'players-list')) {
      routes.push('/players');
    }

    return routes;
  }

  getFirstAllowedRoute(): string {
    const routes = this.getAllowedRoutes();
    return routes.length ? routes[0] : '/no-access';
  }

  // CLEAR (logout)
  clear() {
    this.permissions = {};
    this.permissions$.next({});
    this.lastToken = '';
    localStorage.removeItem('permissions');
    localStorage.removeItem('permissionsSetAt');
  }
}